import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Logo from "../../common/Logo";
import userService from "../../../services/userService";
import ConfirmModal from "../../common/ConfirmModal";
import Notification from "../../common/Notification";

// responsive assets (generate via scripts/generate-images.js)
import set480Webp from "../../../assets/set-password-480.webp";
import set800Webp from "../../../assets/set-password-800.webp";
import set1200Webp from "../../../assets/set-password-1200.webp";
import set480Jpg from "../../../assets/set-password-480.jpg";
import set800Jpg from "../../../assets/set-password-800.jpg";
import set1200Jpg from "../../../assets/set-password-1200.jpg";

const SetPassword = () => {
  const { resetPassword } = userService();
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isDisabled, setIsDisabled] = useState(true);
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-_#$]).{8,16}$/;
  const [error, setError] = useState("");
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    headerText: "Confirm Change password",
    bodyText: "Are you sure you want to change password?",
    btnConfirmText: "Confirm",
    btnCancelText: "Cancel",
  });

  const [notification, setNotification] = useState({
    isOpen: false,
    message: "",
    type: "success",
  });

  const { user, flow } = location.state || {};

  useEffect(() => {
    if (!user || !flow) {
      navigate("/ForgetPassword");
    }
  }, [user, flow, navigate]);

  useEffect(() => {
    var isValid = newPassword !== "";

    if (isValid)
      isValid = confirmPassword !== "";

    if (isValid)
      isValid = passwordRegex.test(newPassword);

    if (isValid)
      isValid = newPassword === confirmPassword;

    setIsDisabled(!isValid);
  }, [newPassword, confirmPassword]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setConfirmModal((prev) => ({ ...prev, isOpen: true }));
  });

  const confirmChangePassword = useCallback(async () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
    try {
      const response = await resetPassword(user.id, newPassword, confirmPassword);
      console.log("Password changed successfully", response);

      if (flow === "changePassword") {
        setNotification({ isOpen: true, message: "Password changed successfully!", type: "success" });
        setTimeout(() => {
          navigate("/home", { state: { fromSetPassword: true } });
        }, 1000);
      } else if (flow === "forgetPassword") {
        navigate("/Success", { state: { fromSetPassword: true } });
      }
    } catch (error) {
      if (flow === "changePassword") {
        setNotification({ isOpen: true, message: error.message, type: "error" });
      }
      setError(error.message);
    }
  });

  const onBackClick = () => {
    if (flow === "changePassword") {
      navigate('/home'); 
    }
    else if (flow === "forgetPassword") {
      navigate('/ForgetPassword');       
    }
    
  };

  const cancelChangePassword = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <section className="bg-tg-bg py-8 lg:py-[50px] min-h-screen">
      <div className="container mx-auto">
        <div className="flex justify-center mb-6"> <Logo /></div>
        <div className="bg-white rounded-lg overflow-hidden shadow-md">
          <div className="flex flex-col lg:flex-row items-stretch">
            {/* responsive banner: stacks above form on small screens, left column on lg */}
            <div className="w-full lg:w-1/2">
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${set480Webp} 480w, ${set800Webp} 800w, ${set1200Webp} 1200w`}
                  sizes="(max-width: 640px) 480px, (max-width: 1024px) 800px, 1200px"
                />
                <source
                  type="image/jpeg"
                  srcSet={`${set480Jpg} 480w, ${set800Jpg} 800w, ${set1200Jpg} 1200w`}
                  sizes="(max-width: 640px) 480px, (max-width: 1024px) 800px, 1200px"
                />
                <img
                  src={set1200Jpg}
                  alt="Set password banner"
                  loading="lazy"
                  className="w-full h-40 sm:h-64 lg:h-full object-cover"
                />
              </picture>
            </div>

            {/* form column */}
            <div className="w-full lg:w-1/2">
              <div className="w-full px-6 py-10 sm:p-12 lg:p-14">
                <h2 className="text-2xl sm:text-3xl font-bold text-dark mb-4">
                  Set Password
                </h2>
                <form onSubmit={handleSubmit}>
                  <InputBox
                    labelTitle="Enter new password"
                    type="password"
                    name="newPassword"
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  />
                  <InputBox
                    labelTitle="Confirm new password"
                    type="password"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  />
                  {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

                  {/* password rules - compact/collapsed (smaller typography) */}
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowPasswordRules((s) => !s)}
                      aria-expanded={showPasswordRules}
                      aria-controls="password-rules"
                      className="flex items-center gap-2 text-left w-full text-xs font-medium text-slate-700"
                    >
                      <span className="text-xs text-red-500">accessibility:</span>
                      <svg
                        className={`w-3 h-3 transition-transform ${showPasswordRules ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    <div
                      id="password-rules"
                      className={`mt-1 overflow-hidden transition-[max-height] duration-200 ${showPasswordRules ? "max-h-40" : "max-h-0"}`}
                    >
                      <ul className="list-disc list-inside ml-3 text-[11px] leading-tight text-slate-500 space-y-0.5">
                        <li className="py-0.5 text-red-500">8-16 characters long</li>
                        <li className="py-0.5 text-red-500">At least one uppercase letter</li>
                        <li className="py-0.5 text-red-500">At least one lowercase letter</li>
                        <li className="py-0.5 text-red-500">At least one number</li>
                        <li className="py-0.5 text-red-500">At least one special character (-, _, #, $)</li>
                      </ul>
                    </div>
                  </div>

                  {/* buttons: vertical on mobile, side-by-side on sm+ */}
                  <div className="mt-8 mb-4 mx-0 sm:mx-4 flex flex-col sm:flex-row justify-center gap-3">
                    
                    <div className="w-full sm:w-1/2 px-3">
                      <button
                        type="submit"
                        className={`w-full rounded-md border p-3 text-white ${isDisabled ? "bg-gray-400 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"}`}
                        disabled={isDisabled}
                      >
                        Change Password
                      </button>
                    </div>
                    <div className="w-full sm:w-1/2 px-3">
                      <button
                        type="button"
                        // className="block w-full rounded-md border border-stroke p-3 text-center text-base font-medium text-dark transition hover:border-red-600 hover:bg-red-600 hover:text-white"
                          // className="block w-full rounded-md border border-stroke p-3 text-center text-base font-medium text-slate-800 transition-colors duration-150 hover:border-red-600 hover:bg-red-600 hover:text-white z-10"
                          className="relative z-20 block w-full rounded-md border border-stroke p-3 text-center text-base font-medium text-slate-800 transition-colors duration-150 hover:!border-red-600 hover:!bg-red-600 hover:!text-white focus:outline-none"

                        onClick={onBackClick}
                      >
                        Back
                      </button>
                    </div>
                  </div>

                  <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    onConfirm={confirmChangePassword}
                    onCancel={cancelChangePassword}
                    headerText={confirmModal.headerText}
                    bodyText={confirmModal.bodyText}
                    btnConfirmText={confirmModal.btnConfirmText}
                    btnCancelText={confirmModal.btnCancelText}
                  />
                  <Notification
                    isOpen={notification.isOpen}
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification({ ...notification, isOpen: false })}
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SetPassword;

// Keep InputBox as-is or make minor responsive tweaks if desired
const InputBox = ({ type, name, labelTitle, value, onChange }) => {
  return (
    <div className="mt-5">
      <label className="mb-2.5 block text-base text-dark font-medium">
        {labelTitle}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full rounded-md border border-[#E9EDF4] bg-transparent px-5 py-3 text-body-color outline-none focus:border-primary focus-visible:shadow-none"
        required
      />
    </div>
  );
};
